// iOS WKWebView bridge for document creators.
//
// The iOS Docs tab opens web creators full-screen in `DocumentsWebViewController`
// (claude-docs/2026-09-14-documents-on-web-and-server-renders.md, Part 1). That
// host registers the handlers below; each helper feature-detects its handler
// and falls back to plain browser behaviour, so the same code runs on desktop,
// in an older app build, and in the WebView. The message names and payloads
// are a FROZEN contract with shipped app builds: add optional fields only.
//
// Why a WebView needs them at all: a WKWebView has no download UI and opens
// nothing for `window.open` / `target="_blank"`, so "View PDF" is a dead button
// there unless the host presents the document itself.

import { bytesToBase64 } from './iosPdfBridge';

const hostHandler = (name) => window.webkit?.messageHandlers?.[name];

/** Whether the page runs inside a host that presents documents natively. */
export const hostPresentsDocuments = () => Boolean(hostHandler('openDocument'));

/** Best-effort MIME type from a (Firebase Storage) URL's object path. */
export function mimeTypeForUrl(url) {
  let pathname;
  try {
    pathname = decodeURIComponent(new URL(url).pathname).toLowerCase();
  } catch {
    return undefined;
  }
  if (pathname.endsWith('.pdf')) return 'application/pdf';
  if (pathname.endsWith('.png')) return 'image/png';
  if (/\.jpe?g$/.test(pathname)) return 'image/jpeg';
  return undefined;
}

/**
 * Tell the host a document entry was saved to the project. The iOS project
 * screen listens to the project doc, so this is a nudge (onboarding, toasts),
 * not the only way the new row appears.
 */
export function notifyDocumentSaved({ docType, firestoreKey, url, name }) {
  const handler = hostHandler('documentSaved');
  if (!handler) return;
  handler.postMessage({
    docType,
    firestoreKey,
    url: url ?? null,
    name: name ?? null,
  });
}

/** Open a saved document: natively in the iOS host, else a new browser tab. */
export function openDocumentUrl(url, { name, mimeType } = {}) {
  if (!url) return;
  const handler = hostHandler('openDocument');
  if (handler) {
    handler.postMessage({
      url,
      name: name || 'Document',
      mimeType: mimeType || mimeTypeForUrl(url) || null,
    });
    return;
  }
  window.open(url, '_blank');
}

/**
 * Open an in-memory PDF (a preview that was never uploaded): the host gets the
 * bytes, since it cannot reach a `blob:` URL; a browser gets a new tab.
 */
export async function openPdfBlob(blob, name) {
  const handler = hostHandler('openDocument');
  if (!handler) {
    window.open(URL.createObjectURL(blob), '_blank');
    return;
  }
  const bytes = new Uint8Array(await blob.arrayBuffer());
  handler.postMessage({
    url: null,
    name: name || 'Document.pdf',
    mimeType: 'application/pdf',
    pdfBase64: bytesToBase64(bytes),
  });
}

/**
 * Whether the host provides its own way out of this screen — its native Done
 * button. When it does, a page control that only exits is a duplicate.
 */
export const hostOwnsExit = () => Boolean(hostHandler('exitToHost'));

/** Whether the host offers a native share sheet for links. */
export const hostSharesLinks = () => Boolean(hostHandler('shareLink'));

/**
 * Offer a link to the native share sheet. Returns false when there is no host
 * handler, so the caller keeps its browser behaviour (copy to clipboard).
 */
export function shareLinkInHost(url, title) {
  const handler = hostHandler('shareLink');
  if (!handler || !url) return false;
  handler.postMessage({ url, title: title || '' });
  return true;
}
